import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import {
  getCMS1500FormById,
  Cms1500Form as ServiceCms1500Form,
} from '../../services/cms1500Service';
import { Cms1500Form } from '../../types';
import { useClaims } from '../../contexts/ClaimsContext';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchInput: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  searchButton: {
    minWidth: 'auto',
  },
  resultsList: {
    maxHeight: '400px',
    overflow: 'auto',
  },
  listItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    backgroundColor: theme.palette.action.focus,
  },
  noResults: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
}));

interface SearchCMS1500ClaimsProps {
  onSelectClaim: (claim: ServiceCms1500Form) => void;
}

const SearchCMS1500Claims: React.FC<SearchCMS1500ClaimsProps> = ({
  onSelectClaim,
}) => {
  const classes = useStyles();
  const [policyNumber, setPolicyNumber] = useState<string>('');
  interface FormSearchResult {
    claimId: string;
    formId: string;
    policyNumber: string;
  }

  const [searchResults, setSearchResults] = useState<FormSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [claimFormId, setClaimFormId] = useState<string | null>(null);

  // Use the Claims context
  const { getClaimsByPolicyNumber, selectClaim } = useClaims();

  const handleSearch = async () => {
    if (!policyNumber.trim()) return;

    setIsSearching(true);
    setHasSearched(true);
    setSearchResults([]);

    try {
      // Get claims by policy number from context
      const claimsByPolicy = await getClaimsByPolicyNumber(policyNumber);

      if (claimsByPolicy.length === 0) {
        setIsSearching(false);
        return;
      }

      // For each claim, get its CMS-1500 forms
      const formResults: FormSearchResult[] = [];

      for (const claim of claimsByPolicy) {
        // Filter out CMS1500 forms from the claim's medical forms
        const cms1500Forms = claim.medicalForms.filter(
          (form): form is Cms1500Form => 'policyNumber' in form
        );

        if (cms1500Forms && cms1500Forms.length > 0) {
          // Add each form to the results
          cms1500Forms.forEach((form) => {
            formResults.push({
              claimId: claim.id.toString(),
              formId: form.id.toString(),
              policyNumber: claim.policyReferenceNo,
            });
          });
        }
      }

      setSearchResults(formResults);
    } catch (error) {
      console.error('Error searching for claims:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectForm = async (result: FormSearchResult) => {
    setIsSearching(true);

    try {
      // Fetch the complete form using the claim ID and form ID
      const form = await getCMS1500FormById(result.claimId, result.formId);

      // Pass the form to the parent component
      selectClaim(result.claimId);
      setClaimFormId(result.formId);
      onSelectClaim(form);
    } catch (error) {
      console.error('Error fetching form details:', error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="h6" gutterBottom>
        Search CMS-1500 Claims
      </Typography>
      <Box className={classes.searchBox}>
        <TextField
          className={classes.searchInput}
          label="Policy Number"
          variant="outlined"
          size="small"
          value={policyNumber}
          onChange={(e) => setPolicyNumber(e.target.value)}
          onKeyUp={handleKeyPress}
        />
        <Button
          className={classes.searchButton}
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={isSearching || !policyNumber.trim()}
          startIcon={
            isSearching ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SearchIcon />
            )
          }
        >
          {isSearching ? 'Searching...' : 'Search'}
        </Button>
      </Box>

      {isSearching ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : hasSearched ? (
        searchResults.length > 0 ? (
          <List className={classes.resultsList}>
            {searchResults.map((result, index) => (
              <React.Fragment key={`${result.claimId}-${result.formId}`}>
                <ListItem
                  className={claimFormId === result.formId ? classes.selectedItem : classes.listItem}
                  onClick={() => handleSelectForm(result)}
                  button
                  disabled={isSearching}
                >
                  <ListItemText
                    primary={`Policy: ${result.policyNumber}`}
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="textPrimary"
                        >
                          Claim ID: {result.claimId}
                          {claimFormId === result.formId ? ' (Selected)' : null}
                        </Typography>
                        <br />
                        Form ID: {result.formId}
                      </>
                    }
                  />
                </ListItem>
                {index < searchResults.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box className={classes.noResults}>
            <Typography variant="body1">
              No claims found for this policy number.
            </Typography>
          </Box>
        )
      ) : null}
    </Paper>
  );
};

export default SearchCMS1500Claims;
